import React, { useState, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { FaArrowRight, FaChevronRight, FaChevronLeft } from "react-icons/fa6";
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Pagination, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import "./NewsLetter.css";

const SLIDES_DATA = [
  {
    id: 1,
    category: "01 / INSIGHTS & ENGINEERING",
    title: "BLOGS",
    badge: "45+ ARTICLES PUBLISHED",
    image:
      "https://images.unsplash.com/photo-1503387762-592deb58ef4e?auto=format&fit=crop&w=1200&q=80",
    description:
      "In-depth technical articles on green concrete formulations, 4D BIM infrastructure modeling, robotic site surveys, and sustainable urbanization trends.",
    tags: ["#GreenBuilding", "#4DBIMTech", "#PreFabConcrete"],
    path: "/blogs",
    buttonText: "EXPLORE BLOGS",
  },
  {
    id: 2,
    category: "02 / PRESS & CORPORATE HIGHLIGHTS",
    title: "NEWS ARTICLES",
    badge: "28+ PRESS FEATURES",
    image:
      "https://images.unsplash.com/photo-1585829365295-ab7cd400c167?auto=format&fit=crop&w=1200&q=80",
    description:
      "Official press releases, national news headlines, major expressway tender wins, and corporate financial performance highlights.",
    tags: ["#NationalPress", "#TenderAwards", "#InfraDaily"],
    path: "/newsarticles",
    buttonText: "VIEW NEWS ARTICLES",
  },
  {
    id: 3,
    category: "03 / BROADCAST & VIDEO FEATURES",
    title: "DIGITAL MEDIA COVERAGE",
    badge: "18+ BROADCAST VIDEOS",
    image:
      "https://images.unsplash.com/photo-1517245386807-bb43f82c33c4?auto=format&fit=crop&w=1200&q=80",
    description:
      "Exclusive CNBC TV18 broadcast interviews, 4K mega bridge construction walkthroughs, and online news portal video features.",
    tags: ["#CNBCTV18", "#4KDocumentary", "#LiveInterviews"],
    path: "/digitalmediacoverage",
    buttonText: "WATCH DIGITAL MEDIA",
  },
];

const NewsLetter = () => {
  const [realIndex, setRealIndex] = useState(0);
  const swiperRef = useRef(null);
  const navigate = useNavigate();

  return (
    <div className="newsletter-page">
      <Helmet>
        <title>Newsletter | Bharat Buildcon</title>
      </Helmet>
      <div className="slider-ambient-glow"></div>

      {/* HEADER SECTION */}
      <section className="slider-header-container">
        <div className="clean-breadcrumb-wrapper">
          <nav className="about-style-breadcrumb" aria-label="Breadcrumb">
            <Link to="/" className="bc-home-link">
              HOME
            </Link>
            <span className="bc-slash">/</span>
            <span className="bc-active-page">NEWSLETTERS</span>
          </nav>
        </div>

        <h1 className="slider-main-title">
          Newsletters & <span className="gradient-text">Media Showcase</span>
        </h1>

        <p className="slider-subtitle-text">
          Use the interactive slider controls to explore our technical blogs,
          press release archives, and broadcast video coverage.
        </p>
      </section>

      {/* CAROUSEL SLIDER WRAPPER */}
      <div className="carousel-outer-wrapper">
        {/* Top Controls Bar */}
        <div className="carousel-top-bar">
          <div className="carousel-counter-badge">
            <span>0{realIndex + 1}</span> / 0{SLIDES_DATA.length}
          </div>

          <div className="carousel-nav-buttons">
            <button
              className="nav-arrow-btn"
              onClick={() => swiperRef.current?.slidePrev()}
              aria-label="Previous Slide"
            >
              <FaChevronLeft />
            </button>
            <button
              className="nav-arrow-btn"
              onClick={() => swiperRef.current?.slideNext()}
              aria-label="Next Slide"
            >
              <FaChevronRight />
            </button>
          </div>
        </div>

        {/* Track Container */}
        <div className="slider-track-container">
          <Swiper
            onBeforeInit={(swiper) => {
              swiperRef.current = swiper;
            }}
            modules={[Navigation, Pagination, Autoplay]}
            slidesPerView={1}
            spaceBetween={30}
            loop={true}
            autoplay={{
              delay: 4500,
              disableOnInteraction: false,
              pauseOnMouseEnter: true,
            }}
            onSlideChange={(swiper) => setRealIndex(swiper.realIndex)}
            className="newsletter-swiper"
          >
            {SLIDES_DATA.map((slide) => (
              <SwiperSlide key={slide.id}>
                <div className="slide-card">
                  <div className="slide-media-box">
                    <img
                      src={slide.image}
                      alt={slide.title}
                      className="slide-img"
                      onError={(e) => {
                        e.target.onerror = null;
                        e.target.src =
                          "https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?auto=format&fit=crop&w=1200&q=80";
                      }}
                    />
                    <div className="slide-live-badge">
                      <span className="pulse-dot"></span> {slide.badge}
                    </div>
                  </div>

                  <div className="slide-content-box">
                    <span className="slide-category-tag">{slide.category}</span>
                    <h2 className="slide-title">{slide.title}</h2>
                    <p className="slide-description">{slide.description}</p>

                    <div className="slide-tags-row">
                      {slide.tags.map((tag, idx) => (
                        <span key={idx} className="slide-tag-chip">
                          {tag}
                        </span>
                      ))}
                    </div>

                    <button
                      className="slide-cta-btn"
                      onClick={() => navigate(slide.path)}
                    >
                      {slide.buttonText} <FaArrowRight className="cta-arrow" />
                    </button>
                  </div>
                </div>
              </SwiperSlide>
            ))}
          </Swiper>
        </div>

        {/* Dots Pagination */}
        <div className="carousel-pagination-dots">
          {SLIDES_DATA.map((_, index) => (
            <button
              key={index}
              className={`dot-btn ${realIndex === index ? "active" : ""}`}
              onClick={() => swiperRef.current?.slideToLoop(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default NewsLetter;
